// JARVIS Auto-Learn Pipeline
// Called after every analysis (vision scan or chat).
// Distributes learnings to: Rules, Patterns, Behaviors, Psychology, Strategies, Predictions.
// No API calls — pure local storage. Runs in <5ms.

import {
  getRules, saveRule,
  getBehaviors, saveBehavior,
  getPatterns, savePattern,
  savePsychologyLog,
  savePrediction, getPredictions,
  getKnowledgeChunks,
} from './storage';

import { getStrategies, saveStrategy } from '../engines/jarvisBrain';

// ── Dedup helpers ──────────────────────────────────────────────────────────
function ruleExists(seedId, title) {
  const rules = getRules();
  return rules.some(r => r.seed_id === seedId || r.title?.toLowerCase() === title?.toLowerCase());
}

function patternExists(seedId, title) {
  const pats = getPatterns();
  return pats.some(p => p.seed_id === seedId || p.title?.toLowerCase() === title?.toLowerCase());
}

function behaviorExists(title) {
  return getBehaviors().some(b =>
    b.title?.toLowerCase().slice(0, 20) === title?.toLowerCase().slice(0, 20)
  );
}

function strategyExists(title) {
  return getStrategies().some(s => s.title?.toLowerCase() === title?.toLowerCase());
}

function makeSeedId(prefix, text) {
  return `${prefix}_${(text || '').toLowerCase().replace(/[^a-z0-9]/g, '_').slice(0, 30)}`;
}

// ── 1. Auto-sync Knowledge chunks → Strategies ────────────────────────────
// Converts knowledge chunks with category='strategy' into brain strategies.
export function syncKnowledgeToStrategies() {
  const chunks = getKnowledgeChunks();
  const stratChunks = chunks.filter(c =>
    c.category === 'strategy' || c.category === 'checklist' ||
    (c.tags && (c.tags.includes('strategy') || c.tags.includes('system')))
  );

  let added = 0;
  for (const chunk of stratChunks) {
    if (strategyExists(chunk.title)) continue;

    saveStrategy({
      title:        chunk.title,
      description:  chunk.content.slice(0, 300),
      conditions:   chunk.tags || [],
      entry_logic:  chunk.content.slice(0, 150),
      sl_logic:     'Below OB / swing low',
      tp_logic:     'Next liquidity pool',
      source:       `Knowledge Base: ${chunk.source || 'JSON Upload'}`,
      auto_generated: true,
    });
    added++;
  }
  return added;
}

// ── 2. Auto-learn from chart analysis result ───────────────────────────────
export function autoLearnFromAnalysis(analysis, source = 'Live Monitor') {
  if (!analysis) return { rules: 0, patterns: 0, behaviors: 0, predictions: 0 };

  const {
    trade_plan = {},
    setup_score = 0,
    grade = 'C',
    rules_applied = [],
    candle_pattern,
    structure,
    trend,
    bias,
    key_observation = '',
    symbol = 'XAUUSD',
    timeframe = 'H1',
    price = 0,
    liquidity = '',
  } = analysis;

  const action = trade_plan.action || bias || 'WAIT';
  let rulesAdded = 0, patternsAdded = 0, behaviorsAdded = 0, predsAdded = 0;

  // ─── Rules from rules_applied list ─────────────────────────────────
  for (const ruleName of rules_applied) {
    if (!ruleName || ruleName.length < 4) continue;
    const sid = makeSeedId('auto_rule', ruleName);
    if (ruleExists(sid, ruleName)) continue;

    saveRule({
      seed_id:   sid,
      title:     ruleName,
      condition: `${structure || ''} structure + ${candle_pattern || ''} + ${trend || ''} trend`,
      action:    action,
      source:    `${source} — ${symbol} ${timeframe}`,
      auto_extracted: true,
      notes:     `Auto-detected during chart analysis. Score: ${setup_score}. Key: ${key_observation.slice(0, 80)}`,
    });
    rulesAdded++;
  }

  // ─── Pattern from candle + structure ───────────────────────────────
  if (candle_pattern && candle_pattern !== 'None' && candle_pattern !== 'none') {
    const patTitle = `${candle_pattern} — ${structure || 'structure'} on ${timeframe}`;
    const sid = makeSeedId('auto_pat', patTitle);

    if (!patternExists(sid, patTitle)) {
      savePattern({
        seed_id:   sid,
        title:     patTitle,
        category:  action === 'BUY' ? 'bullish_entry' : action === 'SELL' ? 'bearish_entry' : 'observation',
        description: `${candle_pattern} appeared at $${price} during ${trend} trend. Structure: ${structure}. ${key_observation.slice(0, 120)}`,
        conditions: [candle_pattern, structure, trend, timeframe].filter(Boolean),
        best_session: 'Any',
        auto_discovered: true,
      });
      patternsAdded++;
    }
  }

  // ─── Behavior from liquidity / structure observation ───────────────
  if (key_observation && key_observation.length > 10) {
    const behavTitle = `${bias || trend || 'Market'} bias — ${structure || 'observation'}`;
    if (!behaviorExists(behavTitle)) {
      saveBehavior({
        title:        behavTitle,
        category:     'market_structure',
        description:  key_observation,
        conditions:   [trend, structure, `Score: ${setup_score}`].filter(Boolean),
        typical_outcome: action,
        learned_from: `${source} — ${symbol} scan`,
      });
      behaviorsAdded++;
    }
  }

  // ─── Liquidity as behavior ──────────────────────────────────────────
  if (liquidity && liquidity !== 'None visible' && liquidity.length > 5) {
    const liqTitle = `Liquidity: ${liquidity.slice(0, 40)}`;
    if (!behaviorExists(liqTitle)) {
      saveBehavior({
        title:        liqTitle,
        category:     'liquidity',
        description:  `Observed: ${liquidity}. Price at $${price}. Action signal: ${action}`,
        conditions:   [liquidity],
        typical_outcome: action,
        learned_from: `${source} — ${symbol}`,
      });
      behaviorsAdded++;
    }
  }

  // ─── Psychology log — always update today's entry ──────────────────
  savePsychologyLog({
    session_note:     key_observation.slice(0, 200),
    market_structure: structure || '',
    bias_detected:    bias || '',
    setup_score:      setup_score,
    grade:            grade,
    action_signal:    action,
    source:           source,
  });

  // ─── Prediction — only save A/A+ BUY/SELL signals ──────────────────
  if (['BUY', 'SELL'].includes(action) && ['A+', 'A'].includes(grade)) {
    const today = new Date().toISOString().slice(0, 10);
    const alreadyToday = getPredictions().some(p =>
      p.date === today && p.direction === action && Math.abs((p.price_at_prediction || 0) - price) < 5
    );
    if (!alreadyToday) {
      savePrediction({
        direction:           action,
        grade:               grade,
        confidence_given:    setup_score,
        price_at_prediction: price,
        reply_summary:       `${key_observation.slice(0, 100)} | Entry: ${trade_plan.entry} | SL: ${trade_plan.sl} | TP: ${trade_plan.tp1}`,
      });
      predsAdded++;
    }
  }

  return { rules: rulesAdded, patterns: patternsAdded, behaviors: behaviorsAdded, predictions: predsAdded };
}

// ── 3. Auto-learn from chat response ──────────────────────────────────────
// Call after every JARVIS chat reply that mentions a trade signal.
export function autoLearnFromChat(userMsg, jarvisReply) {
  if (!jarvisReply || jarvisReply.length < 20) return;

  // Detect if reply has a BUY/SELL signal
  const hasBuy  = /\bBUY\b/i.test(jarvisReply);
  const hasSell = /\bSELL\b/i.test(jarvisReply);
  const hasWait = /\bWAIT\b/i.test(jarvisReply);
  if (!hasBuy && !hasSell && !hasWait) return; // Not a trading response

  const action = hasBuy ? 'BUY' : hasSell ? 'SELL' : 'WAIT';

  // Extract prices from reply (patterns like $3285 or 3285.00)
  const priceMatches = jarvisReply.match(/\$?([\d,]+\.?\d{0,2})/g) || [];
  const prices = priceMatches.map(p => parseFloat(p.replace(/[$,]/g, ''))).filter(p => p > 1000 && p < 10000);

  // Auto-save psychology log from chat
  savePsychologyLog({
    session_note:  `Chat — User asked: "${userMsg.slice(0, 80)}". JARVIS: ${action} signal. ${jarvisReply.slice(0, 120)}`,
    bias_detected: action,
    source:        'Chat interaction',
  });

  // If high-confidence signal with price data, save prediction
  if ((hasBuy || hasSell) && prices.length >= 2) {
    const entry = prices[0];
    const today = new Date().toISOString().slice(0, 10);
    const alreadyToday = getPredictions().some(p =>
      p.date === today && p.direction === action && Math.abs((p.price_at_prediction || 0) - entry) < 5
    );
    if (!alreadyToday) {
      savePrediction({
        direction:           action,
        grade:               'B',
        confidence_given:    65,
        price_at_prediction: entry,
        reply_summary:       `Chat: ${jarvisReply.slice(0, 100)}`,
      });
    }
  }
}

// ── 4. Bootstrap — run on app start ───────────────────────────────────────
// Seeds strategies from knowledge base + ensures rules are up to date.
export function bootstrapJarvisMemory() {
  const strategiesAdded = syncKnowledgeToStrategies();
  return { strategiesAdded };
}
